import { ListCollapse, Users, Settings } from "lucide-react";
import type React from "react";
import { useState } from "react";
import NairaIcon from "../navigation/NairaIcon";
import NavLink from "../navigation/NavLink";
import NavGroup from "../navigation/NavGroup";
import HeaderLogo from "../navigation/HeaderLogo";
import AccountProfile from "../navigation/AccountProfile";
import type { RootState } from "../../../store";
import { useSelector } from "react-redux";
import Logout from "./Logout";

interface SidebarProps {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  userName?: string;
  userEmail?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ sidebarOpen, toggleSidebar }) => {
  const merchant = useSelector(
    (state: RootState) => state?.merchant?.permissions
  ) as string[] | undefined;

  const [activeLink, setActiveLink] = useState("/payments/load");
  const [openGroups, setOpenGroups] = useState<string[]>([
    "Payments",
    "Settlements",
  ]);

  const handleLinkClick = (path: string) => {
    setActiveLink(path);
  };

  const toggleGroup = (groupTitle: string) => {
    setOpenGroups((prevGroups) =>
      prevGroups.includes(groupTitle)
        ? prevGroups.filter((group) => group !== groupTitle)
        : [...prevGroups, groupTitle]
    );
  };

  // Check if user has specific permissions
  const hasPermission = (permission: string): boolean => {
    return Array.isArray(merchant) && merchant.includes(permission);
  };

  // Permission checks for each section
  const canViewTransactions = hasPermission("CAN_VIEW_TRANSACTIONS");
  const canViewSettlements = hasPermission("CAN_VIEW_SETTLEMENTS");
  const canViewCustomers = hasPermission("CAN_VIEW_CUSTOMERS");

  return (
    <div
      className={`fixed lg:relative bg-white z-30 h-full text-gray-800 shadow-lg transition-all duration-300 ease-in-out flex flex-col rounded-r-lg ${
        sidebarOpen ? "w-72 left-0" : "-left-72 lg:left-0 w-0 lg:w-72"
      }`}
    >
      {/* Header with logo */}
      <HeaderLogo toggleSidebar={toggleSidebar} />

      {/* Account Profile Section */}
      <AccountProfile />

      {/* Navigation */}
      <nav className="flex-1 py-4">
        <div className="mb-6">
          <p className="px-6 text-xs uppercase font-semibold text-gray-500 mb-2">
            Main Menu
          </p>

          {canViewTransactions && (
            <NavGroup
              title="Payments"
              icon={<NairaIcon />}
              iconColor=""
              isOpen={openGroups.includes("Payments")}
              onToggle={() => toggleGroup("Payments")}
            >
              <NavLink
                to="/dashboard/payments/load"
                label="Load Payments"
                isActive={activeLink === "/payments/load"}
                onClick={() => handleLinkClick("/payments/load")}
              />
            </NavGroup>
          )}

          {canViewSettlements && (
            <NavGroup
              title="Settlements"
              icon={<ListCollapse size={18} className="text-black" />}
              iconColor=""
              isOpen={openGroups.includes("Settlements")}
              onToggle={() => toggleGroup("Settlements")}
            >
              <NavLink
                to="/dashboard/settlements/load"
                label="Load Settlements"
                isActive={activeLink === "/settlements/load"}
                onClick={() => handleLinkClick("/settlements/load")}
              />
            </NavGroup>
          )}
        </div>

        <div className="mb-6">
          <p className="px-6 text-xs uppercase font-semibold text-gray-500 mb-2">
            Management
          </p>

          {canViewCustomers && (
            <NavLink
              to="/dashboard/customers"
              label="Customers"
              icon={
                <div className="p-1.5 rounded-full">
                  <Users size={16} className="text-black" />
                </div>
              }
              isActive={activeLink === "/dashboard/customers"}
              onClick={() => handleLinkClick("/customers")}
            />
          )}

          <NavLink
            to="/dashboard/settings?tab=profile"
            label="Settings"
            icon={
              <div className="p-1.5 rounded-full">
                <Settings size={16} className="text-black" />
              </div>
            }
            isActive={activeLink === "/dashboard/settings?tab=profile"}
            onClick={() => handleLinkClick("/dashboard/settings?tab=profile")}
          />
        </div>
      </nav>

      {/* Logout Button */}
      <Logout />
    </div>
  );
};

export default Sidebar;
